import { createServerFn } from "@tanstack/react-start";
import * as db from "@/lib/db.server";
import { logger } from "@/lib/logger.server";
import { INSURANCES } from "@/data/heallexa";

/**
 * The insurance catalog: carriers and the plans each one offers. Feeds BOTH
 * pickers — the doctor's "which plans do I accept" multi-select and the
 * patient's carrier -> plan search filter — so the two sides can never drift
 * onto different vocabularies.
 *
 * Tokens, and why plans are flat strings
 * --------------------------------------
 * `providers.insurance` is text[] with a GIN index, and all four search
 * functions already filter with `_insurance = ANY (p.insurance)`. A plan id
 * embeds its carrier ("aetna:choice-pos-ii"), so plan-level filtering needs no
 * join table and no change to the search SQL. A provider row carries tokens at
 * both levels:
 *
 *     {'aetna', 'aetna:choice-pos-ii', 'selfpay'}
 *
 * The doctor picker ALWAYS writes the carrier token alongside any plan token,
 * so a carrier-level search still finds doctors who only listed specific plans.
 * See aws-migration/sql/2026-07-24-insurance-catalog.sql.
 *
 * 'selfpay' is a reserved token, not a carrier row — it is the id already used
 * by INSURANCES in src/data/heallexa.ts, so it is already present in
 * providers.insurance today.
 */

export const SELF_PAY_ID = "selfpay";

export type CatalogCarrier = {
  id: string;
  name: string;
  short: string;
  color: string;
  category: "commercial" | "medicare" | "medicaid" | "military";
};

export type CatalogPlan = {
  id: string;
  carrier_id: string;
  name: string;
  plan_type: string | null;
};

export type InsuranceCatalog = {
  carriers: CatalogCarrier[];
  plans: CatalogPlan[];
};

/**
 * Fallback when insurance_carriers has not been seeded yet (fresh database, or
 * the migration has not been applied to this environment). The pickers keep
 * working at carrier level off the hardcoded list rather than rendering empty.
 * 'selfpay' is filtered out — it is offered as its own checkbox, not a carrier.
 */
function fallbackCarriers(): CatalogCarrier[] {
  return INSURANCES.filter((i) => i.id !== SELF_PAY_ID).map((i) => ({
    id: i.id,
    name: i.name,
    short: i.short,
    color: i.color,
    category: "commercial" as const,
  }));
}

// ponytail: module-level cache per warm Lambda container. The catalog changes
// when an admin approves a plan — minutes-stale is fine, and this runs on every
// search page load. Matches the pattern in search-providers.functions.ts.
let _cache: { data: InsuranceCatalog; expires: number } | undefined;
let _loggedFailure = false;
const CACHE_TTL_MS = 300_000;

export const listInsuranceCatalogFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<InsuranceCatalog> => {
    if (_cache && _cache.expires > Date.now()) return _cache.data;

    try {
      const carriers = await db.query<CatalogCarrier>(
        `SELECT id, name, short, color, category
           FROM public.insurance_carriers
          WHERE is_active
          ORDER BY popularity DESC, name ASC`,
      );

      // Unapproved plans are provider-submitted and awaiting admin review; they
      // must not appear in either picker until promoted.
      const plans = await db.query<CatalogPlan>(
        `SELECT id, carrier_id, name, plan_type
           FROM public.insurance_plans
          WHERE is_approved
          ORDER BY carrier_id ASC, name ASC`,
      );

      const data: InsuranceCatalog = {
        carriers: carriers.length ? carriers : fallbackCarriers(),
        plans,
      };
      _cache = { data, expires: Date.now() + CACHE_TTL_MS };
      return data;
    } catch (err) {
      // A missing table (migration not applied to this environment) must not
      // break search or the doctor's profile form.
      //
      // Cache the fallback too, and log at most once per container: this runs
      // on every search page load, so logging each failure buries the real
      // errors under thousands of identical stacks (observed in dev before the
      // migration was applied). 42P01 = undefined_table, the expected
      // "migration not applied yet" case; anything else is a genuine fault and
      // stays at error level.
      const fallback = { carriers: fallbackCarriers(), plans: [] };
      if (!_loggedFailure) {
        _loggedFailure = true;
        const code = (err as { code?: string })?.code;
        if (code === "42P01") {
          logger.warn(
            "[insurance-catalog] insurance_carriers/insurance_plans missing — " +
              "serving static carriers. Apply aws-migration/sql/2026-07-24-insurance-catalog.sql.",
          );
        } else {
          logger.error("[insurance-catalog] query failed, serving static carriers", err);
        }
      }
      _cache = { data: fallback, expires: Date.now() + CACHE_TTL_MS };
      return fallback;
    }
  },
);

/**
 * Expand a picker selection into the token array stored in providers.insurance.
 *
 * Selecting a plan implies its carrier: a doctor who lists only
 * "aetna:choice-pos-ii" must still be found by a patient who filtered on plain
 * "aetna". Without this the carrier-level search — the one most patients use —
 * would silently miss them.
 *
 * Pure and exported so both the doctor form and the admin listing form use the
 * same rule. Tested in insurance-catalog.test.ts.
 */
export function expandInsuranceTokens(selected: string[]): string[] {
  const out = new Set<string>();
  for (const token of selected) {
    const t = token.trim();
    if (!t) continue;
    out.add(t);
    const colon = t.indexOf(":");
    if (colon > 0) out.add(t.slice(0, colon));
  }
  return [...out];
}
